/* Isolated Quarter zip behaviour. Uses Shopify's cart API and Horizon's cart:update contract. */
(() => {
  if (customElements.get('gripped-quarter-zip')) return;
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cartRoot = () => window.Shopify?.routes?.root || '/';
  const fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, { ...options, headers: { Accept: 'application/json', ...options.headers } });
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Please try again.');
    return data;
  };
  class QuarterZip extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.variants = JSON.parse(this.querySelector('[data-qz-variants]')?.textContent || '[]');
      this.discountConfig = JSON.parse(this.querySelector('[data-qz-discount-tiers]')?.textContent || '{}');
      this.copy = JSON.parse(document.getElementById('GrippedQzTranslations')?.textContent || '{}');
      this.strings = JSON.parse(this.querySelector('[data-qz-strings]')?.textContent || '{}');
      this.form = this.querySelector('.gqz-form');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.track = this.querySelector('[data-gallery-track]');
      this.allSlides = [...this.querySelectorAll('.gqz-gallery__slide')];
      this.selectedQuantity = Number(this.querySelector('[name=quantity]:checked')?.value || 1);
      this.packChoices = [];
      this.colourIndex = Number(this.querySelector('[data-colour-option]')?.dataset.optionIndex ?? -1);
      this.formatter = new Intl.NumberFormat(this.dataset.currency === 'GBP' ? 'en-GB' : (this.dataset.locale || 'en-GB'), {
        style: 'currency', currency: this.dataset.currency || 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 2
      });
      this.classList.add('is-enhanced');
      this.querySelector('[data-enhanced-options]')?.removeAttribute('hidden');
      this.addEventListener('change', this.onChange.bind(this), { signal: this.signal });
      this.addEventListener('click', this.onClick.bind(this), { signal: this.signal });
      this.form?.addEventListener('submit', this.onSubmit.bind(this), { signal: this.signal });
      this.track?.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.updateDots());
      }, { passive: true, signal: this.signal });
      this.track?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); this.goToSlide(this.activeSlide() + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, { signal: this.signal });
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); }, { signal: this.signal });
        dialog.addEventListener('close', () => { this.dialogTrigger?.focus({ preventScroll: true }); }, { signal: this.signal });
      });
      window.addEventListener('popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        const variant = this.variants.find(v => String(v.id) === id) || this.variants[0];
        if (variant) this.selectVariant(variant, false, true);
      }, { signal: this.signal });
      this.selectVariant(this.variants.find(v => String(v.id) === this.variantInput?.value) || this.variants[0], false, true);
      this.querySelectorAll('[data-bundle-item]').forEach(row => {
        const options = row.querySelector('[data-bundle-fields]');
        if (options) {
          options.hidden = false;
          row.querySelector('[data-bundle-native]').hidden = true;
          this.updateBundleRow(row, true);
        }
      });
      this.updateBundle();
      const addButton = this.querySelector('[data-add]');
      const sticky = this.querySelector('[data-sticky]');
      const updateSticky = () => {
        const rect = addButton?.getBoundingClientRect();
        sticky?.classList.toggle('is-past-cta', !!rect && rect.height > 0 && rect.bottom <= 0);
      };
      const scheduleSticky = () => {
        cancelAnimationFrame(this.stickyFrame);
        this.stickyFrame = requestAnimationFrame(updateSticky);
      };
      // A CTA below the viewport must not reveal the sticky, including on fast scrolls.
      this.visibilityObserver = new IntersectionObserver(updateSticky, { threshold: 0 });
      if (addButton) this.visibilityObserver.observe(addButton);
      window.addEventListener('scroll', scheduleSticky, { passive: true, signal: this.signal });
      window.addEventListener('resize', scheduleSticky, { passive: true, signal: this.signal });
      updateSticky();
      this.breakpoint = matchMedia('(max-width: 999px)');
      this.breakpoint.addEventListener('change', () => this.goToSlide(0, false), { signal: this.signal });
    }
    disconnectedCallback() {
      this.galleryAbort?.abort();
      this.abort?.abort(); this.abort = null;
      this.visibilityObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.stickyFrame);
    }
    money(cents) {
      const amount = (Number(cents) || 0) / 100;
      if ((this.dataset.currency || 'EUR') === 'EUR') {
        return this.formatter.formatToParts(amount).filter(part => part.type !== 'currency').map(part => part.value).join('').trim() + '\u00a0€';
      }
      return this.formatter.format(amount);
    }
    t(text) { const key = ({ Schwarz: 'Black', Weiß: 'White', Weiss: 'White', Grau: 'Grey' })[text] || text; return this.copy?.[key] || key; }
    setText(selector, text) { this.querySelectorAll(selector).forEach(el => { el.textContent = text; }); }
    onChange(event) {
      const target = event.target;
      if (target.matches('[data-pack-colour]')) {
        const row = target.closest('[data-pack-row]'), index = Number(row.dataset.packRow);
        this.packChoices[index] = this.packChoice(index);
        this.packChoices[index][this.colourIndex] = target.value;
        this.updatePackRows(); this.updatePrices();
      } else if (target.closest('[data-option-index]')) {
        const choices = [...this.querySelectorAll('[data-option-index]')].map(field => field.querySelector('input:checked')?.value);
        const variant = this.variants.find(v => v.options.every((value, index) => value === choices[index]));
        this.selectVariant(variant, true, false);
      } else if (target.name === 'quantity') {
        this.selectedQuantity = Number(target.value); this.updatePackRows(); this.updatePrices();
      } else if (target.matches('[data-variant-id]')) {
        this.selectVariant(this.variants.find(v => String(v.id) === target.value), true, true);
      } else if (target.matches('[data-selling-plan]')) {
        this.updatePrices();
      } else if (target.matches('[data-bundle-include]')) {
        this.updateBundle();
      } else if (target.matches('[data-bundle-option-index],[data-bundle-variant]')) {
        this.updateBundleRow(target.closest('[data-bundle-item]'), target.matches('[data-bundle-variant]'));
        this.updateBundle();
      }
    }
    selectVariant(variant, updateURL, syncOptions) {
      const oldVariant = this.variant;
      this.variant = variant;
      if (this.variantInput) this.variantInput.value = variant ? String(variant.id) : '';
      if (variant && syncOptions) {
        this.querySelectorAll('[data-option-index]').forEach(field => {
          field.querySelectorAll('input').forEach(input => { input.checked = input.value === variant.options[Number(field.dataset.optionIndex)]; });
        });
      }
      const choices = [...this.querySelectorAll('[data-option-index]')].map(field => field.querySelector('input:checked')?.value);
      this.querySelectorAll('[data-option-index]').forEach(field => {
        const index = Number(field.dataset.optionIndex);
        const valueLabel = field.querySelector('[data-option-value]');
        if (valueLabel) valueLabel.textContent = this.t(choices[index] || '');
        field.querySelectorAll('.gqz-choice').forEach(label => {
          const input = label.querySelector('input');
          const available = this.variants.some(v => v.available && v.options.every((value, j) => value === (j === index ? input.value : choices[j])));
          label.classList.toggle('is-unavailable', !available);
        });
        if (field.hasAttribute('data-colour-option')) {
          const selected = field.querySelector('input:checked')?.closest('.gqz-swatch');
          const row = field.querySelector('.gqz-option__values');
          if (selected && row) {
            const itemRect = selected.getBoundingClientRect(), rowRect = row.getBoundingClientRect();
            if (itemRect.left < rowRect.left) row.scrollLeft += itemRect.left - rowRect.left;
            else if (itemRect.right > rowRect.right) row.scrollLeft += itemRect.right - rowRect.right;
          }
        }
      });
      if (updateURL) {
        const url = new URL(location.href);
        if (variant) url.searchParams.set('variant', variant.id); else url.searchParams.delete('variant');
        history.replaceState(history.state, '', url);
      }
      if (variant) {
        const { min = 1, max, increment = 1 } = variant.quantity_rule || {};
        const radios = [...this.querySelectorAll('[data-quantity-card] input')];
        const validQuantity = this.selectedQuantity >= min && (this.selectedQuantity - min) % increment === 0 && (!max || this.selectedQuantity <= max);
        if (!validQuantity) this.selectedQuantity = min;
        if (!radios.some((_, i) => min + i * increment === this.selectedQuantity)) this.selectedQuantity = min;
        radios.forEach((radio, i) => {
          const q = min + i * increment;
          radio.value = q; radio.disabled = !!max && q > max; radio.checked = q === this.selectedQuantity;
          radio.closest('[data-quantity-card]').querySelector('[data-quantity-buy]').textContent = `${this.t('Buy')} ${q}`;
        });
        if (oldVariant?.id !== variant.id) this.updateSellingPlans();
      }
      this.packChoices[0] = variant ? [...variant.options] : choices;
      this.updatePackRows();
      this.updatePrices();
      this.filterGallery();
    }
    findPackVariant(choices) {
      return this.variants.find(v => v.options.every((value, index) => value === choices?.[index]));
    }
    packChoice(index) {
      // Only colour varies by item. Size and any other product options stay shared.
      return (this.packChoices[0] || []).map((value, optionIndex) => optionIndex === this.colourIndex ? (this.packChoices[index]?.[optionIndex] ?? value) : value);
    }
    packVariants(quantity = this.selectedQuantity) {
      return Array.from({length: quantity}, (_, index) => index === 0 ? this.variant : this.findPackVariant(this.packChoice(index)));
    }
    packLines(quantity = this.selectedQuantity) {
      const lines = new Map();
      this.packVariants(quantity).forEach(variant => {
        if (!variant) return;
        const line = lines.get(variant.id) || {variant, quantity: 0};
        line.quantity++; lines.set(variant.id, line);
      });
      return [...lines.values()];
    }
    packProblem() {
      const variants = this.packVariants(), planID = this.querySelector('[data-selling-plan]')?.value;
      if (variants.some(v => !v)) return this.t('Choose colours available in your selected size, or change the size for the whole pack.');
      if (variants.some(v => !v.available)) return this.t('A selected colour is sold out in this size. Choose another colour or change the size for the whole pack.');
      if ((planID || this.hasAttribute('data-requires-plan')) && variants.some(v => !v.selling_plan_allocations?.some(p => String(p.id) === planID))) return this.t('This purchase option is not available for every selected item.');
      for (const {variant, quantity} of this.packLines()) {
        const {min = 1, max, increment = 1} = variant.quantity_rule || {};
        if (quantity < min || (quantity - min) % increment !== 0 || (max && quantity > max)) return `The quantity for ${variant.title} must follow its minimum (${min}) and increments (${increment})${max ? `, up to ${max}` : ''}.`;
      }
      return '';
    }
    updatePackRows() {
      const pack = this.querySelector('[data-pack]'), container = this.querySelector('[data-pack-items]'), source = this.querySelector('[data-colour-option]');
      if (!pack || !container || !source) return;
      pack.hidden = this.selectedQuantity <= 1;
      source.querySelector('[data-colour-prefix]')?.toggleAttribute('hidden', pack.hidden);
      this.querySelector('[data-pack-size-note]')?.toggleAttribute('hidden', pack.hidden);
      const extraCount = Math.max(0, this.selectedQuantity - 1);
      while (container.children.length > extraCount) container.lastElementChild.remove();
      while (container.children.length < extraCount) {
        const row = source.cloneNode(true), index = container.children.length + 1;
        row.removeAttribute('data-option-index'); row.removeAttribute('data-colour-option'); row.setAttribute('data-pack-row', index);
        row.classList.add('gqz-pack__row');
        const legend = row.querySelector('legend');
        legend.replaceChildren(document.createTextNode(`${this.t('Colour')} ${index + 1}: `));
        const value = document.createElement('span'); value.dataset.packColourValue = ''; legend.append(value);
        row.querySelectorAll('input').forEach(input => { input.name = `${this.id}-pack-colour-${index}`; input.setAttribute('data-pack-colour', ''); });
        const status = document.createElement('p'); status.dataset.packRowStatus = ''; status.setAttribute('role', 'status'); status.hidden = true; row.append(status);
        container.append(row);
      }
      [...container.children].forEach((row, offset) => {
        const index = offset + 1;
        this.packChoices[index] = this.packChoice(index);
        const choices = this.packChoices[index], variant = this.findPackVariant(choices);
        row.dataset.packRow = index;
        row.querySelector('[data-pack-colour-value]').textContent = this.t(choices[this.colourIndex] || '');
        row.querySelectorAll('[data-pack-colour]').forEach(input => {
          input.checked = input.value === choices[this.colourIndex];
          const available = this.variants.some(v => v.available && v.options.every((value, i) => value === (i === this.colourIndex ? input.value : choices[i])));
          input.closest('.gqz-swatch').classList.toggle('is-unavailable', !available);
          input.setAttribute('aria-label', this.t(input.value) + (available ? '' : ' — ' + this.t('Sold out in the selected size')));
        });
        const status = row.querySelector('[data-pack-row-status]');
        status.hidden = !!variant?.available;
        status.textContent = !variant ? this.t('This colour is unavailable in the selected size.') : !variant.available ? this.t('Sold out in the selected size.') : '';
        this.revealSwatch(row);
      });
      this.querySelectorAll('[data-option-index]:not([data-colour-option])').forEach(field => {
        const optionIndex = Number(field.dataset.optionIndex);
        field.querySelectorAll('.gqz-choice').forEach(label => {
          const input = label.querySelector('input');
          const available = Array.from({length: this.selectedQuantity}, (_, index) => this.packChoice(index)).every(choices => this.variants.some(v => v.available && v.options.every((value, i) => value === (i === optionIndex ? input.value : choices[i]))));
          label.classList.toggle('is-unavailable', !available);
        });
      });
    }
    revealSwatch(field) {
      const selected = field.querySelector('input:checked')?.closest('.gqz-swatch'), track = field.querySelector('.gqz-option__values');
      if (!selected || !track) return;
      const item = selected.getBoundingClientRect(), bounds = track.getBoundingClientRect();
      if (item.left < bounds.left) track.scrollLeft += item.left - bounds.left;
      else if (item.right > bounds.right) track.scrollLeft += item.right - bounds.right;
    }
    freeShipping(total) { return this.dataset.currency === 'EUR' && total >= Math.max(0, Number(this.dataset.freeShippingMin) || 0); }
    updateSellingPlans() {
      const select = this.querySelector('[data-selling-plan]');
      if (!select) return;
      const previous = select.value;
      const plans = this.variant?.selling_plan_allocations || [];
      select.replaceChildren();
      if (!this.hasAttribute('data-requires-plan')) select.add(new Option(this.t('One-time purchase'), ''));
      plans.forEach(plan => select.add(new Option(`${plan.name} — ${this.money(plan.price)}`, String(plan.id))));
      if ([...select.options].some(o => o.value === previous)) select.value = previous;
    }
    tierPercent(quantity) {
      if (!this.discountConfig.enabled) return 0;
      return Math.max(0, ...(this.discountConfig.tiers || []).filter(tier => quantity >= Number(tier.quantity)).map(tier => Math.max(0, Math.min(50, Number(tier.percent) || 0))));
    }
    quote(quantity) {
      if (!this.variant) return {unit: 0, total: 0, saving: 0, percent: 0};
      const planID = this.querySelector('[data-selling-plan]')?.value;
      const percent = planID ? 0 : this.tierPercent(quantity);
      let subtotal = 0, total = 0, compareTotal = 0, tierBest = true;
      for (const {variant, quantity: count} of this.packLines(quantity)) {
        const plan = variant.selling_plan_allocations?.find(p => String(p.id) === planID);
        const base = Number(plan?.price ?? variant.price), line = base * count;
        const breaks = [...(variant.quantity_price_breaks || [])].sort((a, b) => a.minimum_quantity - b.minimum_quantity);
        const nativeUnit = planID ? base : Number(breaks.filter(b => count >= b.minimum_quantity).at(-1)?.price ?? base);
        const tierTotal = line - Math.round(line * percent / 100);
        subtotal += line; total += Math.min(nativeUnit * count, tierTotal);
        compareTotal += (planID ? base : Math.max(base, Number(variant.compare_at_price) || 0)) * count;
        tierBest &&= tierTotal <= nativeUnit * count;
      }
      return {unit: total / quantity, total, compareTotal, saving: subtotal - total, retailSaving: compareTotal - total, percent: tierBest ? percent : 0};
    }
    unitPrice(quantity) { return this.quote(quantity).unit; }
    updatePrices() {
      const variant = this.variant;
      const quantity = this.selectedQuantity;
      const problem = this.packProblem(), available = !problem;
      const packStatus = this.querySelector('[data-pack-status]');
      if (packStatus) { packStatus.hidden = !problem; packStatus.textContent = problem; }
      const label = !variant ? this.strings.unavailable : !available ? this.strings.soldOut : this.strings.add;
      this.querySelectorAll('[data-add],[data-sticky-add]').forEach(button => { button.disabled = this.busy || !available; });
      this.setText('[data-add-label]', label);
      this.setText('[data-sticky-add]', label);
      this.setText('[data-quantity-label]', `${quantity} ${quantity === 1 ? this.strings.item : this.strings.items}`);
      const availability = this.querySelector('[data-availability]');
      if (availability) availability.hidden = !available;
      if (!variant) {
        this.setText('[data-price]', '—');
        this.querySelectorAll('[data-compare],[data-saving],[data-discount],[data-add-prices]').forEach(el => el.hidden = true);
        return;
      }
      const quote = this.quote(quantity), price = Number(variant.price), compare = Number(variant.compare_at_price) || 0;
      const onSale = compare > price;
      this.setText('[data-price]', this.money(price));
      this.setText('[data-compare]', this.money(compare));
      this.setText('[data-saving]', `${this.strings.save} ${this.money(compare - price)}`);
      this.setText('[data-discount]', `${onSale ? Math.round((compare - price) / compare * 100) : 0}% OFF`);
      this.setText('[data-add-price]', this.money(quote.total));
      this.setText('[data-add-compare]', this.money(quote.compareTotal));
      this.querySelectorAll('[data-compare],[data-saving],[data-discount]').forEach(el => { el.hidden = !onSale; });
      this.querySelectorAll('[data-add-compare]').forEach(el => { el.hidden = quote.retailSaving <= 0; });
      this.querySelectorAll('[data-add-prices]').forEach(el => el.hidden = false);
      this.querySelectorAll('[data-quantity-card]').forEach(card => {
        const qty = Number(card.querySelector('input').value), offer = this.quote(qty);
        const bestValue = card.querySelector('[data-quantity-best-value]');
        if (bestValue) bestValue.hidden = qty !== 4 || card.querySelector('input').disabled;
        card.querySelector('[data-unit-price]').textContent = this.money(offer.unit);
        card.querySelector('[data-total-price]').textContent = `${this.money(offer.total)} ${this.strings.total}`;
        const saving = card.querySelector('[data-quantity-saving]');
        saving.hidden = offer.retailSaving <= 0;
        saving.textContent = `${this.strings.save} ${this.money(offer.retailSaving)}`;
        const shipping = card.querySelector('[data-quantity-shipping]');
        if (shipping) shipping.hidden = !this.freeShipping(offer.total);
        const percent = card.querySelector('[data-quantity-percent]');
        if (percent) { percent.hidden = !this.discountConfig.enabled; percent.style.visibility = offer.percent ? 'visible' : 'hidden'; percent.textContent = offer.percent ? `${this.t('Save')} ${offer.percent}%` : '';  }
      });
      document.querySelectorAll('[data-qz-value]').forEach(section => {
        const set = (selector, text) => section.querySelectorAll(selector).forEach(el => el.textContent = text);
        const reference = Number(section.dataset.referencePrice), comparable = this.dataset.currency === 'EUR' && reference > 0;
        set('[data-value-price]', this.money(variant.price));
        set('[data-value-saving]', `${this.money(reference - variant.price)} ${this.t('less')}`);
        set('[data-value-discount]', `${Math.max(0, Math.round((reference - variant.price) / reference * 100))}% ${this.t('less')}`);
        section.querySelectorAll('[data-value-sale]').forEach(el => el.hidden = !comparable || reference <= variant.price);
        section.querySelector('[data-value-bar]')?.style.setProperty('--bar-height', `${comparable ? Math.min(320, Math.max(20, variant.price / Math.max(reference, variant.price) * 320)) : 160}px`);
        section.querySelector('[data-reference-bar]')?.style.setProperty('--bar-height', `${comparable ? Math.min(320, Math.max(20, reference / Math.max(reference, variant.price) * 320)) : 320}px`);
      });
    }
    filterGallery() {
      if (this.querySelector('[data-qz-native-gallery]')) {
        this.updateNativeGallery();
        return;
      }
      const photoNote = this.querySelector('[data-qz-photo-note]');
      if (photoNote) photoNote.hidden = !!this.variant?.featured_media_id;
      const colourField = this.querySelector('[data-colour-option]');
      const colour = colourField?.querySelector('input:checked')?.value?.toLowerCase();
      this.allSlides.forEach(slide => {
        const match = (slide.dataset.mediaAlt || '').match(/#colou?r_(.+?)(?:#|$)/i);
        slide.hidden = !!(colour && match && match[1].trim().toLowerCase() !== colour);
      });
      this.slides = this.allSlides.filter(slide => !slide.hidden);
      if (!this.slides.length) { this.allSlides.forEach(slide => slide.hidden = false); this.slides = this.allSlides; }
      this.track?.toggleAttribute('data-single-media', this.slides.length === 1);
      const controls = this.querySelector('[data-gallery-controls]');
      if (controls) controls.hidden = this.slides.length <= 1;
      const dots = this.querySelector('[data-gallery-dots]');
      if (dots) {
        dots.replaceChildren(...this.slides.map((_, index) => {
          const button = document.createElement('button'); button.type = 'button';
          button.dataset.galleryDot = index; button.setAttribute('aria-label', `Image ${index + 1}`); return button;
        }));
      }
      const variantSlide = this.slides.findIndex(slide => slide.dataset.mediaId === String(this.variant?.featured_media_id));
      const selected = variantSlide >= 0 ? variantSlide : 0;
      requestAnimationFrame(() => this.goToSlide(selected, false));
    }
    async updateNativeGallery() {
      const host = this.querySelector('[data-qz-native-gallery]');
      if (!host) return;
      const photoNote = host.querySelector('[data-qz-photo-note]');
      if (photoNote) photoNote.hidden = !!this.variant?.featured_media_id;
      const mediaId = String(this.variant?.featured_media_id || '');
      // Native Horizon variant media selection is rendered by Shopify, keeping
      // the gallery, mobile carousel and zoom dialog in the same media order.
      this.galleryAbort?.abort();
      if (host.dataset.featuredMediaId !== mediaId && this.variant) {
        this.galleryAbort?.abort();
        this.galleryAbort = new AbortController();
        const request = this.galleryAbort;
        const url = new URL(location.href);
        url.searchParams.set('variant', this.variant.id);
        url.searchParams.set('section_id', this.dataset.sectionId);
        try {
          const response = await fetch(url, {signal: request.signal});
          if (!response.ok) throw new Error('Gallery unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const replacement = html.querySelector('[data-qz-native-gallery]');
          if (request.signal.aborted || request !== this.galleryAbort || !this.isConnected || String(this.variant?.featured_media_id || '') !== mediaId) return;
          if (replacement?.querySelector('media-gallery')) host.replaceWith(replacement);
        } catch (error) {
          if (error.name !== 'AbortError') console.warn('Quarter Zip gallery could not refresh.', error);
        }
      }
      if (String(this.variant?.featured_media_id || '') !== mediaId) return;
      const colour = this.querySelector('[data-colour-option] input:checked')?.value?.toLowerCase();
      const gallery = this.querySelector('[data-qz-native-gallery] media-gallery');
      if (!gallery) return;
      // Honour legacy #color_/ #colour_ media tags as well as Shopify's
      // native attached-variant filtering. Horizon excludes hidden slides.
      const candidates = [...gallery.querySelectorAll('.media-gallery__grid > li, slideshow-slide')];
      for (const item of candidates) {
        const tag = item.querySelector('img')?.alt.match(/#colou?r_(.+?)(?:#|$)/i);
        item.hidden = !!(colour && tag && tag[1].trim().toLowerCase() !== colour);
      }
      const visible = candidates.filter(item => !item.hidden);
      if (!visible.length) candidates.forEach(item => item.hidden = false);
      gallery.slideshow?.select(0, undefined, {animate: false});
    }
    slidePosition(index) {
      if (!this.track || !this.slides?.[index]) return 0;
      const slide = this.slides[index].getBoundingClientRect(), track = this.track.getBoundingClientRect();
      const centre = slide.left - track.left + this.track.scrollLeft - (this.track.clientWidth - slide.width) / 2;
      return Math.max(0, Math.min(centre, this.track.scrollWidth - this.track.clientWidth));
    }
    activeSlide() {
      if (!this.track?.clientWidth || !this.slides?.length) return 0;
      return this.slides.reduce((closest, _, index) => Math.abs(this.slidePosition(index) - this.track.scrollLeft) < Math.abs(this.slidePosition(closest) - this.track.scrollLeft) ? index : closest, 0);
    }
    goToSlide(index, smooth = true) {
      if (!this.track || !this.slides?.length) return;
      index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({ left: this.slidePosition(index), behavior: smooth && !reducedMotion() ? 'smooth' : 'instant' });
      this.updateDots(index);
    }
    updateDots(index = this.activeSlide()) {
      this.querySelectorAll('[data-gallery-dot]').forEach(button => button.setAttribute('aria-current', String(Number(button.dataset.galleryDot) === index)));
      const prev = this.querySelector('[data-gallery-prev]'), next = this.querySelector('[data-gallery-next]');
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= (this.slides?.length || 1) - 1;
    }
    onClick(event) {
      const button = event.target.closest('button,a');
      if (!button) return;
      if (button.matches('[data-gallery-next]')) this.goToSlide(this.activeSlide() + 1);
      if (button.matches('[data-gallery-prev]')) this.goToSlide(this.activeSlide() - 1);
      if (button.matches('[data-gallery-dot]')) this.goToSlide(Number(button.dataset.galleryDot));
      if (button.matches('[data-close-dialog]')) button.closest('dialog')?.close();
      if (button.matches('[data-size-open]')) { this.dialogTrigger = button; this.querySelector('[data-size-dialog]')?.showModal(); }
      if (button.matches('[data-size-unit]')) {
        const guide = button.closest('[data-size-guide]'), unit = button.dataset.sizeUnit;
        guide.querySelectorAll('[data-size-unit]').forEach(toggle => toggle.setAttribute('aria-pressed', String(toggle === button)));
        guide.querySelectorAll('[data-size-table]').forEach(table => table.hidden = table.dataset.sizeTable !== unit);
      }
      if (button.matches('[data-zoom],[data-zoom-current]')) {
        const link = button.matches('[data-zoom]') ? button : this.slides[this.activeSlide()]?.querySelector('[data-zoom]');
        const dialog = this.querySelector('[data-zoom-dialog]');
        if (link && dialog?.showModal) {
          event.preventDefault(); this.dialogTrigger = button;
          const image = dialog.querySelector('[data-zoom-image]'); image.src = link.href; image.alt = link.querySelector('img')?.alt || this.t('Product image'); dialog.showModal();
        }
      }
      if (button.matches('[data-sticky-add]')) {
        const addButton = this.querySelector('[data-add]');
        if (addButton && !addButton.disabled) this.form?.requestSubmit(addButton);
      }
      if (button.matches('[data-add-bundle]')) this.addBundle(button);
    }
    async addItems(items) {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
      const response = await fetchJSON(`${cartRoot()}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname })
      });
      // The mutation succeeded. A subsequent read failure must never invite a duplicate add.
      let cart;
      try { cart = await fetchJSON(`${cartRoot()}cart.js`); } catch { cart = undefined; }
      this.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: {
        resource: cart, sourceId: this.id,
        data: { source: 'product-form-component', productId: this.dataset.productId, itemCount: items.reduce((n, item) => n + item.quantity, 0), sections: response.sections }
      } }));
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer?.open) drawer.open();
      else if (drawer?.showDialog) drawer.showDialog();
      else {
        const status = this.querySelector('[data-form-status]');
        if (status) { status.textContent = `${this.strings.added}. `; const link = document.createElement('a'); link.href = `${cartRoot()}cart`; link.textContent = this.t('View bag'); link.style.textDecoration = 'underline'; status.append(link); }
      }
      return cart;
    }
    async onSubmit(event) {
      event.preventDefault();
      if (this.busy || this.packProblem()) return;
      const formData = new FormData(this.form), items = this.packLines().map(({variant, quantity}) => ({id: variant.id, quantity}));
      if (formData.get('selling_plan')) items.forEach(item => item.selling_plan = Number(formData.get('selling_plan')));
      const properties = {};
      for (const [key, value] of formData.entries()) { const match = key.match(/^properties\[(.+)\]$/); if (match) properties[match[1]] = value; }
      if (Object.keys(properties).length) items.forEach(item => item.properties = properties);
      this.busy = true;
      const error = this.querySelector('[data-form-error]'); if (error) error.hidden = true;
      this.setText('[data-form-status]', '');
      this.querySelectorAll('[data-add],[data-sticky-add]').forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      this.setText('[data-add-label]', this.strings.adding);
      try {
        await this.addItems(items);
        if (!this.querySelector('[data-form-status] a')) this.setText('[data-form-status]', this.strings.added);
      } catch (err) {
        this.setText('[data-form-status]', '');
        if (error) { error.textContent = err.message || this.strings.error; error.hidden = false; }
      } finally {
        this.busy = false; this.querySelectorAll('[aria-busy]').forEach(button => button.removeAttribute('aria-busy')); this.updatePrices();
      }
    }
    updateBundleRow(row, syncOptions = false) {
      if (!row) return;
      const select = row.querySelector('[data-bundle-variant]');
      const fields = [...row.querySelectorAll('[data-bundle-option-index]')];
      const variants = [...select.options].map(option => ({
        id: option.value, options: JSON.parse(option.dataset.bundleOptions),
        available: !option.disabled, price: Number(option.dataset.bundlePrice), image: option.dataset.bundleImage
      }));
      if (syncOptions) {
        const variant = variants.find(v => v.id === select.value);
        if (variant) fields.forEach(field => { field.value = variant.options[Number(field.dataset.bundleOptionIndex)]; });
      }
      const choices = [];
      fields.forEach(field => { choices[Number(field.dataset.bundleOptionIndex)] = field.value; });
      const selected = fields.length ? variants.find(v => v.options.every((value, index) => value === choices[index])) : variants.find(v => v.id === select.value);
      select.value = selected?.id || '';
      fields.forEach(field => {
        const index = Number(field.dataset.bundleOptionIndex);
        [...field.options].forEach(option => {
          const available = variants.some(v => v.available && v.options.every((value, i) => value === (i === index ? option.value : choices[i])));
          option.textContent = this.t(option.value) + (available ? '' : ' — ' + this.t('Sold out'));
        });
      });
      row.querySelector('[data-bundle-item-price]').textContent = selected ? this.money(selected.price) : '—';
      const image = row.querySelector('a img');
      if (selected?.image && image) { image.src = selected.image; image.removeAttribute('srcset'); }
      const status = row.querySelector('[data-bundle-item-status]');
      status.hidden = !!selected?.available;
      status.textContent = !selected ? this.t('This combination is unavailable.') : !selected.available ? this.t('This size is sold out in the selected colour.') : '';
    }
    bundleRows() { return [...this.querySelectorAll('[data-bundle-item]')]; }
    bundleIncluded(row) { return row.querySelector('[data-bundle-include]')?.checked ?? true; }
    updateBundle() {
      const rows = this.bundleRows();
      if (!rows.length) return;
      const selectedRows = rows.filter(row => this.bundleIncluded(row));
      const eligibleRows = selectedRows.filter(row => row.dataset.bundleProductId === this.dataset.productId);
      const percent = this.tierPercent(eligibleRows.length);
      let subtotal = 0, total = 0, unavailable = selectedRows.length === 0;
      rows.forEach(row => {
        const included = this.bundleIncluded(row), select = row.querySelector('[data-bundle-variant]'), selected = select.selectedOptions[0];
        const base = Number(selected?.dataset.bundlePrice || 0);
        const rowPercent = included && eligibleRows.includes(row) ? percent : 0;
        const price = base - Math.round(base * rowPercent / 100);
        row.classList.toggle('is-excluded', !included);
        row.querySelectorAll('select').forEach(field => { field.disabled = !included; });
        const status = row.querySelector('[data-bundle-item-status]');
        status.hidden = !included || !!selected && !selected.disabled;
        status.textContent = !selected ? this.t('This combination is unavailable.') : selected.disabled ? this.t('This size is sold out in the selected colour.') : '';
        row.querySelector('[data-bundle-item-price]').textContent = selected ? this.money(price) : '—';
        const rowSaving = row.querySelector('[data-bundle-row-saving]');
        const saved = Math.max(base, Number(selected?.dataset.bundleCompare) || 0) - price;
        if (rowSaving) { rowSaving.hidden = !included || saved <= 0 || !selected || selected.disabled; rowSaving.textContent = `${this.t('Save')} ${this.money(saved)}`; }
        if (included) { subtotal += base; total += price; unavailable ||= !selected || selected.disabled; }
      });
      const button = this.querySelector('[data-add-bundle]'); if (button) button.disabled = this.busy || unavailable;
      rows.forEach(row => { const shipping = row.querySelector('[data-bundle-row-shipping]'); if (shipping) { shipping.hidden = !this.bundleIncluded(row) || unavailable || !this.freeShipping(total); shipping.title = this.t('Free shipping with this selection'); } });
      this.setText('[data-bundle-total]', `— ${this.money(total)}`);
      const saving = this.querySelector('[data-bundle-saving]');
      if (saving) { saving.hidden = !percent || subtotal === total || unavailable; saving.textContent = `${this.t('Save')} ${percent}% (${this.money(subtotal - total)})`; }
    }
    async addBundle(button) {
      if (this.busy || button.disabled) return;
      this.busy = true; button.disabled = true; button.setAttribute('aria-busy', 'true');
      this.setText('[data-bundle-status]', this.t('Adding your selection…'));
      const items = this.bundleRows().filter(row => this.bundleIncluded(row)).map(row => ({ id: Number(row.querySelector('[data-bundle-variant]').value), quantity: 1 }));
      try { await this.addItems(items); this.setText('[data-bundle-status]', this.strings.added); }
      catch (err) { this.setText('[data-bundle-status]', err.message || this.strings.error); }
      finally { this.busy = false; button.removeAttribute('aria-busy'); this.updateBundle(); }
    }
  }
  customElements.define('gripped-quarter-zip', QuarterZip);
  class QuarterZipStories extends HTMLElement {
    connectedCallback() {
      this.copy = JSON.parse(document.getElementById('GrippedQzTranslations')?.textContent || '{}');
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.clips = [...this.querySelectorAll('video')].map((video, index) => {
        const button = video.closest('[data-qz-video-card]').querySelector('[data-story-toggle]');
        const clip = { video, button, visible: false, manuallyPaused: false, manualPlay: false };
        video.muted = true; video.playsInline = true; video.loop = true;
        video.controls = false; button.hidden = false;
        if (!video.getAttribute('aria-label')) video.setAttribute('aria-label', `${this.copy?.['Gripped styling video'] || 'Gripped styling video'} ${index + 1}`);
        const sync = () => {
          button.setAttribute('aria-label', `${this.copy?.[video.paused ? 'Play video' : 'Pause video'] || (video.paused ? 'Play video' : 'Pause video')} ${index + 1}`);
          button.querySelector('[data-story-play]').toggleAttribute('hidden', !video.paused);
          button.querySelector('[data-story-pause]').toggleAttribute('hidden', video.paused);
        };
        video.addEventListener('play', sync, { signal });
        video.addEventListener('pause', sync, { signal });
        video.addEventListener('error', () => { video.controls = true; button.hidden = true; }, { signal });
        button.addEventListener('click', () => {
          if (video.paused) { clip.manuallyPaused = false; clip.manualPlay = true; video.play().catch(sync); }
          else { clip.manuallyPaused = true; clip.manualPlay = false; video.pause(); }
        }, { signal });
        return clip;
      });
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const clip = this.clips.find(item => item.video === entry.target);
          clip.visible = entry.isIntersecting && entry.intersectionRatio >= 0.35;
          this.updatePlayback(clip);
        });
      }, { threshold: [0, 0.35] });
      this.clips.forEach(clip => this.observer.observe(clip.video));
      document.addEventListener('visibilitychange', () => this.clips.forEach(clip => this.updatePlayback(clip)), { signal });
      this.motion.addEventListener('change', () => this.clips.forEach(clip => { clip.manualPlay = false; this.updatePlayback(clip); }), { signal });
    }
    updatePlayback(clip) {
      const auto = this.dataset.autoplay === 'true' && !this.motion.matches;
      if (clip.visible && !document.hidden && !clip.manuallyPaused && (auto || clip.manualPlay)) clip.video.play().catch(() => {});
      else clip.video.pause();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null; this.observer?.disconnect();
      this.clips?.forEach(clip => clip.video.pause());
    }
  }
  customElements.define('gripped-qz-stories', QuarterZipStories);
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-qz-carousel-step]');
    if (!button) return;
    const track = button.closest('.gqz-collection')?.querySelector('.gqz-products');
    if (track) track.scrollBy({ left: Number(button.dataset.qzCarouselStep) * track.clientWidth, behavior: reducedMotion() ? 'instant' : 'smooth' });
  });
})();
